import { ScrollBarService } from './../../../shared/scroll/scrollbar.service';
import { PagedVirtualScrollerComponent } from '@shared/components/paged-virtual-scroller/paged-virtual-scroller.component';
import { ICategory } from '@collections/categories/interfaces/category.interface';
import { NewsStatusEnum } from '@collections/news/enums/news.enums';
import {
  Component, OnInit, ViewEncapsulation, ChangeDetectionStrategy, ViewChild, ElementRef, PLATFORM_ID, Inject
} from '@angular/core';
import { NewsGQL, NewsResponse } from '@shared/services/news/news.gql.service';
import { NewsTypeEnum } from '@collections/news/enums/news.enums';
import { LanguagesService } from '@shared/services/languages/languages.service';
import { Platform } from '@angular/cdk/platform';
import { FilterNode } from '@shared/components/filters/filters.component';
import { QueryRef } from 'apollo-angular';
import { MetadataService } from '@shared/services/meta-data-service/meta-data.service';
import { PortalEventsGQL, PortalEventsResponse } from 'app/shared/services/portal-events/graphql/portal-events.gql.service';
import { map, of, Subject, take, zip } from 'rxjs';
import { FieldsOfRelevances } from 'app/shared/constants/fields-of-relevance';
import { RootCategoriesGQL } from 'app/shared/services/categories/root-categories.gql.service';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { UsersService } from 'app/shared/services/users/users.service';
import { EventBlockComponent } from '../../../portal-events/views/event-block/event-block.component';
import { NewsBlockSocialComponent } from '../../../news/views/news-block-social/news-block-social.component';
import { PagedVirtualScrollerComponent as PagedVirtualScrollerComponent_1 } from '../../../shared/components/paged-virtual-scroller/paged-virtual-scroller.component';
import { FiltersComponent } from '../../../shared/components/filters/filters.component';
import { CarouselBannerComponent } from '../../../shared/components/carousel-banner/carousel-banner.component';
import { NewsHeaderResponsiveComponent } from './news-headerResponsive/news-headerResponsive.component';
import { NewsHeaderComponent } from './news-header/news-header.component';
import { ToolbarHallsComponent } from '../../../layout/components/toolbar-halls/toolbar-halls.component';
import { RenderIfDirective } from '../../../shared/directives/render-if.directive';
import { isPlatformBrowser } from '@angular/common';
import { OrderType } from '@collections/companies/enums';
import { BreadcrumbsService, IBreadcrumb } from 'app/shared/services/breadcrumbs/breadcrumbs.service';
import { CallToActionComponent } from 'app/shared/components/call-to-action/call-to-action.component';

@UntilDestroy()
@Component({
    selector: 'news-page',
    templateUrl: './news.component.html',
    styleUrls: ['./news.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
      RenderIfDirective, 
      ToolbarHallsComponent, 
      NewsHeaderComponent, 
      NewsHeaderResponsiveComponent, 
      CarouselBannerComponent, 
      FiltersComponent, 
      PagedVirtualScrollerComponent_1, 
      NewsBlockSocialComponent, 
      EventBlockComponent,
      CallToActionComponent
    ]
})
export class NewsComponent implements OnInit {
  breadcrumb: IBreadcrumb[];
  
  treeDataSource = new Subject<FilterNode[]>();
  triggerAnim: boolean;
  itemHeight = 900;
  totalSize: number;
  newsQuery: QueryRef<NewsResponse>;
  eventsQuery: QueryRef<PortalEventsResponse>;

  @ViewChild('psNews', { static: true }) psNews: PagedVirtualScrollerComponent;
  @ViewChild('top', { static: true }) topEl: ElementRef;

  constructor(
    public platform: Platform,
    private newsGQL: NewsGQL,
    private languagesService: LanguagesService,
    private metadataService: MetadataService,
    private portalEventsGQL: PortalEventsGQL,
    private rootCategoriesGQL: RootCategoriesGQL,
    public usersService: UsersService,
    private scrollBarService: ScrollBarService,
    private breadcrumbsService: BreadcrumbsService,
    @Inject(PLATFORM_ID) private platformID: object
  ) {
  }

  ngOnInit(): void {
    this.breadcrumb = [
      {
        label: $localize`News`,
        url: "/news"
      }
    ]
    this.breadcrumbsService.store(this.breadcrumb);

    this.metadataService.appendMetadata({
      title: $localize`:news description|titolo della pagina@@NewsTitle:News and Updates`,
      description: $localize`All the most important industry news and updates: latest products, services, technologies, innovations, and events.`,
      images: ['assets/expometals-social.png'],
      pageType: 'website',
    },
    null,
    'auto');

    this.metadataService.appendRSSLink(true, this.languagesService.getCurrentLangCode())
    this.metadataService.appendRSSLink(false, this.languagesService.getCurrentLangCode())

    if (isPlatformBrowser(this.platformID)){
      const currentDate = new Date().toISOString();
      localStorage.setItem('lastNewsVisit', currentDate);
    }

    this.newsQuery = this.newsGQL.watch({
      lang: this.languagesService.getCurrentLangCode(),
      newsType: NewsTypeEnum.companies,
      newsStatus: NewsStatusEnum.portalLevelValidated,
      first: 3
    });

    this.eventsQuery = this.portalEventsGQL.watch({
      first: 6,
      orderType: OrderType.firstin,
      fromDate: Date.now(),
      isAdmin: false
    });

    const _catsNode = new FilterNode($localize`Categories`);

    const categories$ = this.rootCategoriesGQL.watch({
      lang: this.languagesService.getCurrentLangCode()
    }).valueChanges
    .pipe(
      map(result => result.data.rootCategories),
      map( cats => {
        return cats.map(c => this.buildCategorysNode(c, _catsNode));
      })
    );

    const _fieldsOfRelevanceNode = new FilterNode($localize`Fields of Relevance`);

    const fieldsOfInterest$ = of(FieldsOfRelevances.map((fi) => {
      return  new FilterNode(fi.label, fi.value, null, null, 'fields-of-relevance', _fieldsOfRelevanceNode);
    }));


    zip(
      categories$,
      fieldsOfInterest$
    )
    .pipe(
      take(1),
      map(([categories, fields]) => {
        _catsNode.children = categories;
        _fieldsOfRelevanceNode.children = fields;
        return [
          _fieldsOfRelevanceNode,
          _catsNode
        ];
      }),
      untilDestroyed(this)
    )
    .subscribe(nodes => {
      this.treeDataSource.next(nodes);
    });
  }

  selectSector(sector): void {
    if (document.getElementById(sector).style.color !== 'green') {
      document.getElementById(sector).style.color = 'green';
    }
    else {
      document.getElementById(sector).style.color = '';
    }
  }

  buildCategorysNode(cat: ICategory, parent: FilterNode): FilterNode {
    const node: FilterNode = new FilterNode(cat.shortName, cat.path, null, null, 'category', parent);
    // if (cat.childs) {
    //   node.children = cat.childs.map(c => this.buildCategorysNode(c, node));
    // }
    return node;
  }

  filterNews(nodes: FilterNode[]): void {
    const query = this.psNews.itemsQuery;
    delete query.variables.fieldsOfRelevance;
    delete query.variables.category;
    delete query.variables.after;
    for (const node of nodes) {
      if (node.type === 'fields-of-relevance' ) {
        query.variables.fieldsOfRelevance = [node.value];
      }
      if (node.type === 'category' ) {
        query.variables.category = node.value;
      }
    }

    this.scrollBarService.mainScrollbar.scrollToElement(this.topEl).then(() => query.refetch(query.variables));
  }
  
}

